import React from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  getFeatureSchema,
  getBreadcrumbSchema
} from '../../utils/seoSchemas';

const steps = [
  'Technician completes the job and marks it done',
  'Invoice is generated automatically from the job request',
  'VAT is calculated correctly (20% standard, 0% for MOT)',
  'PDF invoice is created and stored',
  'Invoice is emailed to the customer',
  'Payment status is tracked in the system'
];

const benefits = [
  'No manual invoice creation needed',
  'UK VAT-compliant invoices every time',
  'Professional branded PDF invoices',
  'Automatic email delivery to customers',
  'Edit and resend invoices when needed',
  'Complete invoice history and search'
];

const Invoicing = () => {
  const featureSchema = getFeatureSchema({
    name: 'Invoicing',
    description: 'Automatic VAT-compliant invoice generation for UK garages. Send professional invoices instantly when jobs are completed.',
    url: 'https://www.go-ops.digital/features/invoicing'
  });
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.go-ops.digital/' },
    { name: 'Features', url: 'https://www.go-ops.digital/features' },
    { name: 'Invoicing', url: 'https://www.go-ops.digital/features/invoicing' }
  ]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet>
        <title>Invoicing | Go Ops Features</title>
        <meta
          name="description"
          content="Automatic VAT-compliant invoice generation for UK garages. Send professional invoices instantly when jobs are completed."
        />
        <link rel="canonical" href="https://www.go-ops.digital/features/invoicing" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="Invoicing | Go Ops Features" />
        <meta
          property="og:description"
          content="Automatic VAT-compliant invoice generation for UK garages. Send professional invoices instantly when jobs are completed."
        />
        <meta property="og:url" content="https://www.go-ops.digital/features/invoicing" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify(featureSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Navigation */}
      <Box sx={{ bgcolor: '#2c3e50', py: 2 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{ color: 'white', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Go Ops
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="text" component={RouterLink} to="/features" sx={{ color: 'white' }}>
                Features
              </Button>
              <Button variant="text" component={RouterLink} to="/pricing" sx={{ color: 'white' }}>
                Pricing
              </Button>
              <Button variant="text" component={RouterLink} to="/contact" sx={{ color: 'white' }}>
                Contact
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/auth/login"
                sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
              >
                Login
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 6, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h1" component="h1" sx={{ fontSize: '2.5rem', fontWeight: 'bold', mb: 2 }}>
            Invoicing
          </Typography>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
            Automatic VAT-compliant invoices for UK garages
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 4, lineHeight: 1.8 }}>
          Go Ops automatically generates professional, VAT-compliant invoices the moment a job is completed.
          No more manual invoice creation, calculation errors, or chasing paperwork. Every invoice is stored,
          searchable, and can be resent with one click.
        </Typography>

        {/* How it works */}
        <Paper sx={{ p: 4, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
            How It Works
          </Typography>
          <List>
            {steps.map((step, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: '#ff6b35',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}
                  >
                    {index + 1}
                  </Box>
                </ListItemIcon>
                <ListItemText primary={step} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Benefits */}
        <Paper sx={{ p: 4, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
            Benefits
          </Typography>
          <List>
            {benefits.map((benefit, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#28a745' }} />
                </ListItemIcon>
                <ListItemText primary={benefit} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            See invoicing in action
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact"
              sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
            >
              Book a Demo
            </Button>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/features"
              sx={{ borderColor: '#2c3e50', color: '#2c3e50' }}
            >
              View All Features
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Go Ops. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Button component={RouterLink} to="/features" sx={{ color: 'white', textTransform: 'none' }}>
                Features
              </Button>
              <Button component={RouterLink} to="/pricing" sx={{ color: 'white', textTransform: 'none' }}>
                Pricing
              </Button>
              <Button component={RouterLink} to="/contact" sx={{ color: 'white', textTransform: 'none' }}>
                Contact
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Invoicing;
